// src/utils/exportJobPDF.js
// Called from Dashboard PDF button — fetches job data then opens a print window
import API from "../services/api";
const API_BASE = import.meta.env.VITE_API_URL || "https://thesalonx.in/api";

const STATUS_LABELS = {
  pending: "Pending", ongoing: "Ongoing", hold: "Hold",
  revisit: "Revisit", completed: "Completed",
};

const STATUS_COLORS = {
  pending:   { bg: "#fef3c7", color: "#b45309" },
  ongoing:   { bg: "#dbeafe", color: "#1d4ed8" },
  hold:      { bg: "#ede9fe", color: "#6d28d9" },
  revisit:   { bg: "#ccfbf1", color: "#0f766e" },
  completed: { bg: "#dcfce7", color: "#15803d" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const fmtTime = (t) => {
  if (!t) return "—";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour < 12 ? "AM" : "PM"}`;
};

const calcDuration = (s, e) => {
  if (!s || !e) return "—";
  const diff = (new Date(`1970-01-01T${e}`) - new Date(`1970-01-01T${s}`)) / 60000;
  if (diff <= 0) return "—";
  const h = Math.floor(diff / 60), m = diff % 60;
  return h === 0 ? `${m} min` : `${h}h ${String(m).padStart(2, "0")}m`;
};

// Convert an image URL to base64 so it survives the print window
const toBase64 = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch {
        resolve(url); // fallback to original URL if CORS blocks
      }
    };
    img.onerror = () => resolve(url);
    img.src = url;
  });

export async function exportJobPDF(jobId) {
  // ── 1. Fetch job data ──
  const res = await API.get(`/job/${jobId}`);
  const json = res.data;
  if (!json.success) throw new Error(json.message || "Failed to fetch job");

  const { job, activities, materials, photos } = json.data;
  const statusKey = job.status?.toLowerCase() ?? "";
  const statusLabel = STATUS_LABELS[statusKey] ?? job.status;
  const statusColor = STATUS_COLORS[statusKey] ?? { bg: "#f3f4f6", color: "#374151" };

  // ── 2. Pre-load photos as base64 ──
  const photoSections = [
    { label: "Before", urls: photos?.before ?? [] },
    { label: "During", urls: photos?.during ?? [] },
    { label: "After",  urls: photos?.after  ?? [] },
  ];

  for (const section of photoSections) {
    section.base64 = await Promise.all(section.urls.map(toBase64));
  }

  // ── 3. Build HTML ──
  const infoRows = [
    { label: "Job ID",    value: `#${job.id}` },
    { label: "Client",    value: job.client_name },
    { label: "Location",  value: job.location },
    { label: "Work Type", value: job.work_type },
    { label: "Staff",     value: job.staff_name },
    { label: "Email",     value: job.staff_email },
    { label: "Phone",     value: job.staff_phone },
    { label: "Created",   value: fmtDate(job.created_at) },
  ].filter(r => r.value);

  const infoHTML = infoRows.map(r => `
    <tr>
      <td class="info-label">${r.label}</td>
      <td class="info-value">${r.value}</td>
    </tr>
  `).join("");

  const materialsHTML = (materials ?? []).length > 0 ? `
    <div class="section">
      <h2>Materials Used</h2>
      <table class="data-table">
        <thead><tr><th>Material</th><th>Quantity</th></tr></thead>
        <tbody>
          ${materials.map(m => `
            <tr>
              <td>${m.material_name}</td>
              <td><span class="qty-badge">${m.quantity}</span></td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  ` : "";

  const activitiesHTML = (activities ?? []).length > 0 ? `
    <div class="section">
      <h2>Activity Logs</h2>
      <table class="data-table">
        <thead>
          <tr>
            <th>Activity</th>
            <th>Start</th>
            <th>End</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          ${activities.map(a => `
            <tr>
              <td>${a.activity_type}</td>
              <td>${fmtTime(a.start_time)}</td>
              <td>${fmtTime(a.end_time)}</td>
              <td>${calcDuration(a.start_time, a.end_time)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  ` : "";

  const photosHTML = photoSections.map(section => {
    if (!section.base64?.length) return `
      <div class="photo-group">
        <h3 class="photo-group-title">${section.label} Photos</h3>
        <p class="no-photos">No ${section.label.toLowerCase()} photos uploaded.</p>
      </div>
    `;
    return `
      <div class="photo-group">
        <h3 class="photo-group-title">${section.label} Photos</h3>
        <div class="photo-grid">
          ${section.base64.map((src, i) => `
            <div class="photo-item">
              <img src="${src}" alt="${section.label} ${i + 1}" />
              <div class="photo-caption">${section.label} — ${i + 1}</div>
            </div>
          `).join("")}
        </div>
      </div>
    `;
  }).join("");

  const generatedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Job #${job.id} — Report</title>
  <style>
    /* ── Reset ── */
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
      font-size: 13px;
      color: #111827;
      background: #fff;
      padding: 0;
    }

    /* ── Page layout ── */
    .page-wrapper {
      max-width: 860px;
      margin: 0 auto;
      padding: 40px 48px;
    }

    /* ── Header ── */
    .report-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #111827;
      padding-bottom: 18px;
      margin-bottom: 28px;
    }

    .report-title {
      font-size: 24px;
      font-weight: 700;
      color: #111827;
      margin-bottom: 4px;
    }

    .report-meta {
      font-size: 12px;
      color: #6b7280;
    }

    .report-logo {
      text-align: right;
    }

    .report-logo-name {
      font-size: 18px;
      font-weight: 700;
      color: #2563eb;
    }

    .report-logo-sub {
      font-size: 11px;
      color: #6b7280;
      margin-top: 2px;
    }

    /* ── Status badge ── */
    .status-badge {
      display: inline-block;
      padding: 4px 14px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      background: ${statusColor.bg};
      color: ${statusColor.color};
      margin-top: 8px;
    }

    /* ── Sections ── */
    .section {
      margin-bottom: 32px;
    }

    h2 {
      font-size: 14px;
      font-weight: 700;
      color: #111827;
      margin-bottom: 12px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    /* ── Info table ── */
    .info-table {
      width: 100%;
      border-collapse: collapse;
    }

    .info-label {
      width: 130px;
      padding: 8px 10px 8px 0;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.04em;
      color: #6b7280;
      text-transform: uppercase;
      vertical-align: top;
      border-bottom: 1px solid #f3f4f6;
    }

    .info-value {
      padding: 8px 0;
      font-size: 13px;
      color: #111827;
      border-bottom: 1px solid #f3f4f6;
    }

    .info-table tr:last-child .info-label,
    .info-table tr:last-child .info-value {
      border-bottom: none;
    }

    /* ── Data tables ── */
    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table thead {
      background: #f9fafb;
    }

    .data-table th {
      padding: 9px 12px;
      text-align: left;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.055em;
      color: #6b7280;
      text-transform: uppercase;
      border-bottom: 1px solid #e5e7eb;
    }

    .data-table td {
      padding: 9px 12px;
      font-size: 12.5px;
      color: #374151;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
    }

    .data-table tbody tr:last-child td {
      border-bottom: none;
    }

    .data-table tbody tr:nth-child(even) td {
      background: #fafafa;
    }

    .qty-badge {
      display: inline-block;
      background: #f3f4f6;
      color: #374151;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 12px;
      font-weight: 500;
    }

    /* ── Photos ── */
    .photos-section {
      margin-bottom: 32px;
    }

    .photo-group {
      margin-bottom: 24px;
    }

    h3.photo-group-title {
      font-size: 12.5px;
      font-weight: 600;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 10px;
    }

    .photo-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    .photo-item {
      width: 180px;
      break-inside: avoid;
    }

    .photo-item img {
      width: 180px;
      height: 135px;
      object-fit: cover;
      border-radius: 6px;
      border: 1px solid #e5e7eb;
      display: block;
    }

    .photo-caption {
      font-size: 10px;
      color: #9ca3af;
      text-align: center;
      margin-top: 4px;
    }

    .no-photos {
      font-size: 12px;
      color: #9ca3af;
      font-style: italic;
    }

    /* ── Footer ── */
    .report-footer {
      border-top: 1px solid #e5e7eb;
      margin-top: 40px;
      padding-top: 14px;
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #9ca3af;
    }

    /* ── Print ── */
    @media print {
      body { padding: 0; }
      .page-wrapper { padding: 24px 32px; max-width: 100%; }
      .no-print { display: none !important; }
      h2 { break-after: avoid; }
      .section { break-inside: avoid; }
      .photo-item { break-inside: avoid; }
      .photo-grid { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="page-wrapper">

    <!-- Header -->
    <div class="report-header">
      <div>
        <div class="report-title">Job Report — #${job.id}</div>
        <div class="report-meta">Generated on ${generatedOn}</div>
        <div><span class="status-badge">${statusLabel}</span></div>
      </div>
      <div class="report-logo">
        <div class="report-logo-name">FieldOps</div>
        <div class="report-logo-sub">Job Management System</div>
      </div>
    </div>

    <!-- Basic Information -->
    <div class="section">
      <h2>Basic Information</h2>
      <table class="info-table">
        <tbody>${infoHTML}</tbody>
      </table>
    </div>

    <!-- Materials -->
    ${materialsHTML}

    <!-- Activities -->
    ${activitiesHTML}

    <!-- Photos -->
    <div class="section photos-section">
      <h2>Photos</h2>
      ${photosHTML}
    </div>

    <!-- Footer -->
    <div class="report-footer">
      <span>Job #${job.id} — Confidential</span>
      <span>${generatedOn}</span>
    </div>

  </div>

  <script>
    window.onload = function() {
      window.print();
    };
  </script>
</body>
</html>
  `;

  // ── 4. Open print window ──
  const win = window.open("", "_blank", "width=960,height=720");
  if (!win) {
    alert("Please allow pop-ups to export the PDF.");
    return;
  }
  win.document.write(html);
  win.document.close();
}